import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useActivity } from "@/context/ActivityContext";
import { ShieldCheck, Save } from "lucide-react";

export const MODES = [
  { v: "forbidden", l: "Non autorisé", d: "Bouton masqué, API refusée (aucun code ne contourne)" },
  { v: "code", l: "Autorisé avec code unique", d: "Le caissier prépare l'action, valide avec un code 5 min / usage unique" },
  { v: "free", l: "Autorisé sans code", d: "Action directe : autonomie réelle donnée au caissier" },
];
const useActions = () => {
  const [actions, setActions] = useState([]);
  useEffect(() => { api.get("/sensitive-actions").then((r) => setActions(r.data || [])).catch(() => {}); }, []);
  return actions;
};
const norm = (v) => ({ require: "code", exempt: "free", true: "code", false: "free" }[String(v)] || (["forbidden", "code", "free"].includes(v) ? v : ""));

// Paramètres → règle par défaut de la boutique (activité courante), trois états par action.
export function SensitiveActionsCard() {
  const { current, reload } = useActivity();
  const actions = useActions();
  const [cfg, setCfg] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { const c = {}; Object.entries(current?.sensitive_actions || {}).forEach(([k, v]) => { c[k] = norm(v); }); setCfg(c); }, [current?.id, current?.sensitive_actions]);
  if (!current || current.global) return null;
  const save = async () => {
    setSaving(true);
    try {
      const body = {}; actions.forEach((a) => { body[a.key] = cfg[a.key] || "free"; });
      await api.patch(`/activities/${current.id}`, { name: current.name, template: current.template || "generic", currency: current.currency || "XOF", allow_negative_stock: !!current.allow_negative_stock, track_margin: current.track_margin !== false, stock_mode: current.stock_mode || "simple", sensitive_actions: body });
      toast.success("Actions sensibles enregistrées pour " + current.name); reload?.();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div className="card p-5 space-y-4" data-testid="sensitive-actions-card">
      <div className="flex items-center gap-2 text-[#16324F] font-semibold font-head"><ShieldCheck className="w-5 h-5" /> Actions sensibles — {current.name}</div>
      <p className="text-sm text-[#64748B]">Règle par défaut pour les <b>caissiers</b> de cette activité. Propriétaire, admin et responsable agissent toujours directement. Les remises déjà paramétrées dans le catalogue restent automatiques. Dérogation possible par caissier dans « Utilisateurs ».</p>
      <div className="space-y-2">
        {actions.map((a) => (
          <div key={a.key} className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
            <div className="flex-1 text-sm font-medium text-[#0F172A]">{a.label}</div>
            <select className="inp !py-1.5 sm:!w-64 text-sm" value={cfg[a.key] || "free"} onChange={(e) => setCfg({ ...cfg, [a.key]: e.target.value })} data-testid={`sensitive-mode-${a.key}`}>
              {MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
        ))}
      </div>
      <ul className="text-[11px] text-[#64748B] space-y-0.5">{MODES.map((m) => <li key={m.v}><b>{m.l}</b> : {m.d}</li>)}</ul>
      <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-sensitive-actions-btn"><Save className="w-4 h-4" /> Enregistrer</button>
    </div>
  );
}

// Utilisateurs → dérogation par caissier et par activité : Défaut (règle activité) ou l'un des trois états.
export function SensitiveOverrides({ activityId, value, onChange }) {
  const actions = useActions();
  const cur = value?.[activityId] || {};
  const set = (k, v) => { const next = { ...cur }; if (v === "") delete next[k]; else next[k] = v; onChange({ ...value, [activityId]: next }); };
  return (
    <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3 space-y-2" data-testid="user-sensitive-overrides">
      <div className="text-sm font-medium text-[#0F172A]">Actions sensibles (cette activité)</div>
      <p className="text-[11px] text-[#64748B]">« Défaut » suit la règle de l'activité (Paramètres). Sinon : Non autorisé / Avec code unique / Sans code, pour ce caissier uniquement.</p>
      {actions.map((a) => (
        <div key={a.key} className="flex items-center justify-between gap-2 text-sm">
          <span>{a.label}</span>
          <select className="inp !py-1 !w-48 text-xs" value={norm(cur[a.key])} onChange={(e) => set(a.key, e.target.value)} data-testid={`user-sensitive-${a.key}`}>
            <option value="">Défaut (règle activité)</option>{MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
        </div>
      ))}
    </div>
  );
}
